import { IconType } from 'react-icons';
import {
	LuCircleCheck,
	LuCircleX,
	LuFileQuestion,
	LuFileStack,
	LuListChecks,
	LuPause,
	LuSquarePen,
	LuTrash2,
	LuTriangleAlert,
} from 'react-icons/lu';
import { OfferOutcome, PendingActionMode, PendingActionType, PendingOptionAction } from '@/client';

/** `OfferOutcome` minus `'advanced'` — a selection that produced the next offer isn't its own
 * rendered state (see ChatOfferOutcomeCard), so nothing here needs to give it copy or an icon. */
export type ResolvedOfferOutcome = Exclude<OfferOutcome, 'advanced'>;

/**
 * Narrows an offer's outcome to the ones that render as their own state. An unset outcome is still
 * open, and `'advanced'` is a selection whose result is the next offer — the exchange right after
 * it — so neither gets an outcome card.
 */
export const isResolvedOfferOutcome = (outcome: OfferOutcome | null | undefined): outcome is ResolvedOfferOutcome =>
	!!outcome && outcome !== 'advanced';

/**
 * Footer caption per mode. A `confirm` really does write nothing until this exact reply; a
 * `select` only narrows the target — the backend still shows a fresh confirm offer afterwards
 * (selection is deliberately not confirmation, see backend CORRECTIONS.md), so it gets its own
 * caption rather than reusing "won't save unless confirmed".
 */
export const PENDING_MODE_CAPTION: Record<PendingActionMode, string> = {
	confirm: "Won't save unless confirmed",
	select: "You'll be asked to confirm before anything changes",
};

/**
 * Copy + icon per `pending_type`. The backend only tells us which pending is open — "correction"
 * vs. "new fact" isn't distinguished at this level (see docs/local/chat-feature.md §C2), so every
 * confirm_correction offer renders under one generic title regardless of which it is.
 */
export const PENDING_TYPE_COPY: Record<PendingActionType, { eyebrow: string; title: string; icon: IconType }> = {
	confirm_correction: {
		eyebrow: 'Preparing correction',
		title: 'Correction for review',
		icon: LuSquarePen,
	},
	confirm_deactivate_correction: {
		eyebrow: 'Preparing removal',
		title: 'Remove this correction?',
		icon: LuTrash2,
	},
	select_correction_target: {
		eyebrow: 'Choose a document',
		title: 'Which document is this about?',
		icon: LuFileQuestion,
	},
	select_correction_to_deactivate: {
		eyebrow: 'Choose a correction',
		title: 'Which correction should be removed?',
		icon: LuListChecks,
	},
	// Never "supersession", and never a document id: the backend states the rule for this surface
	// (CORRECTIONS.md §Replacing a document with a newer one), and the household's words for it are
	// using the newer document and setting the older one aside.
	confirm_supersession: {
		eyebrow: 'Preparing replacement',
		title: 'Use the newer document?',
		icon: LuFileStack,
	},
};

/**
 * Our own copy for a `confirm`-mode option, keyed by pending_type then action — "affirm" means
 * "Save" when creating a correction but "Remove" when deactivating one, so the same action needs
 * different wording depending on what's being confirmed. An action/pending_type pair with no entry
 * here falls back to the backend's own `label` — the contract's escape hatch for an action we
 * haven't written copy for yet, so a new one still renders instead of an empty control.
 */
const CONFIRM_ACTION_COPY: Partial<Record<PendingActionType, Partial<Record<PendingOptionAction, string>>>> = {
	confirm_correction: {
		affirm: 'Save',
		decline: 'Cancel',
		replace: 'Replace',
		keep_both: 'Keep both',
	},
	confirm_deactivate_correction: {
		affirm: 'Remove',
		decline: 'Keep it',
	},
	// "Save" would be wrong twice over: this writes no correction of its own, and what it settles is
	// which of two documents answers from now on.
	confirm_supersession: {
		affirm: 'Use the newer one',
		decline: 'Keep both',
	},
};

export const getConfirmOptionCopy = (
	pendingType: PendingActionType,
	action: PendingOptionAction,
	fallback: string,
): string => CONFIRM_ACTION_COPY[pendingType]?.[action] ?? fallback;

/** Actions that read as the affirmative/primary path get a solid button; everything else (declining,
 * or keeping a prior correction alongside a new one) reads as secondary. `select`/`select_all` are
 * rendered by a different, row-shaped control (see ChatPendingActionOption) and never consult this. */
const SOLID_ACTIONS: ReadonlySet<PendingOptionAction> = new Set(['affirm', 'replace']);

export const isPrimaryAction = (action: PendingOptionAction): boolean => SOLID_ACTIONS.has(action);

/**
 * Copy for a turn whose offer has gone (`offeredAction` set, `pendingAction` null — see
 * AssistantQuery.offeredAction). All four reasons an offer disappears — taken, superseded by a new
 * topic, aged out after 2 turns, or the session lapsing after an hour — read identically here: say
 * it's gone, never why. select_correction_target/select_correction_to_deactivate copy is
 * design-approved; confirm_correction/confirm_deactivate_correction follow the same pattern but
 * still need design sign-off.
 */
export const EXPIRED_OFFER_COPY: Record<PendingActionType, string> = {
	confirm_correction: "This correction offer has expired — ask again if you'd still like to save it.",
	confirm_deactivate_correction: "This removal offer has expired — ask again if you'd still like to remove it.",
	select_correction_target: 'This question about which document has expired — ask again.',
	select_correction_to_deactivate: 'This question about which correction has expired — ask again.',
	confirm_supersession:
		"This replacement offer has expired — ask again if you'd still like to use the newer document.",
};

/**
 * Copy for a resolved offer (`offerOutcome` set — see AssistantQuery.offerOutcome), keyed by
 * `offeredAction` then outcome: "taken" means "Saved" for a create-offer but "Removed" for a
 * deactivate-offer, same reason `getConfirmOptionCopy` varies "affirm" by pending_type. A
 * `select_*` offer is only ever resolved through the create/deactivate offer it produces
 * (`'advanced'`, excluded from this table entirely), so it reuses its paired confirm-type's copy —
 * a select outcome should never actually reach `taken`/`declined`/`deferred` directly, but the
 * table stays total rather than partial so a lookup here can never come back empty.
 */
const OFFER_OUTCOME_COPY: Record<PendingActionType, Record<ResolvedOfferOutcome, string>> = {
	confirm_correction: {
		taken: 'Saved — future answers will use this',
		declined: 'Not saved',
		deferred: 'Not saved for now',
		failed: "This couldn't be completed",
	},
	confirm_deactivate_correction: {
		taken: 'Removed',
		declined: 'Kept',
		deferred: 'Not removed for now',
		failed: "This couldn't be completed",
	},
	select_correction_target: {
		taken: 'Saved — future answers will use this',
		declined: 'Not saved',
		deferred: 'Not saved for now',
		failed: "This couldn't be completed",
	},
	select_correction_to_deactivate: {
		taken: 'Removed',
		declined: 'Kept',
		deferred: 'Not removed for now',
		failed: "This couldn't be completed",
	},
	// "Set aside" rather than "deleted": the older document is reversible at any time, and saying so
	// is the difference between a reassuring outcome and an alarming one.
	confirm_supersession: {
		taken: 'Using the newer document — the older one is set aside',
		declined: 'Both documents kept',
		deferred: 'Both documents kept for now',
		failed: "This couldn't be completed",
	},
};

export const getOfferOutcomeCopy = (offeredAction: PendingActionType, outcome: ResolvedOfferOutcome): string =>
	OFFER_OUTCOME_COPY[offeredAction][outcome];

/** One icon per resolved outcome — deliberately the only thing that varies by outcome rather than
 * by `offeredAction`; "taken" reads as success regardless of whether it saved or removed. */
export const OFFER_OUTCOME_ICON: Record<ResolvedOfferOutcome, IconType> = {
	taken: LuCircleCheck,
	declined: LuCircleX,
	deferred: LuPause,
	failed: LuTriangleAlert,
};
